import { MANUFACTURER_IDS } from "./manufacturers.js";
import { TIERS, TIER_STAT_MULT, type Manufacturer, type Tier } from "../schema/common.js";
import { ENHANCE_MAX, ENHANCE_MAX_S, ENHANCE_STEP_PCT, MODULE_SLOTS, type ModuleDef, type ModuleSlot, type ModuleStat } from "../schema/module.js";

/** Main stat per slot with its tier-D base value (percent points, or flat for acc/eva). */
const SLOT_MAIN: Record<ModuleSlot, { stat: ModuleStat; base: number }> = {
  drive: { stat: "eva", base: 4 },
  weapon: { stat: "atk", base: 8 },
  armor: { stat: "hp", base: 8 },
  core: { stat: "crit", base: 3 },
  sensor: { stat: "acc", base: 8 },
};

const SLOT_SUBSTATS: Record<ModuleSlot, ModuleStat[]> = {
  drive: ["def", "hp", "energyPerAttack", "intervalTicks"],
  weapon: ["crit", "critDmg", "atk", "energyPerAttack"],
  armor: ["def", "hp", "eva", "energyPerAttack"],
  core: ["energyPerAttack", "critDmg", "atk", "intervalTicks"],
  sensor: ["intervalTicks", "eva", "crit", "acc"],
};

/**
 * Tier multiplier of module stats. Units keep TIER_STAT_MULT; gear lifts S to three times A (D136, user 27.09.2026:
 * "S-Waffe 3 mal so stark"), so a piece of S gear is the prize it is priced as. Special bonuses keep the unit table.
 */
export const MODULE_TIER_MULT: Record<Tier, number> = { ...TIER_STAT_MULT, S: TIER_STAT_MULT.A * 3 };

/** Substat base values at tier D (percent points, or flat for acc/eva); scaled by the tier multiplier. */
export const SUBSTAT_BASE: Record<ModuleStat, number> = {
  hp: 3,
  atk: 3,
  def: 3,
  crit: 1.5,
  critDmg: 5,
  acc: 4,
  eva: 4,
  energyPerAttack: 4,
  intervalTicks: 2,
};

export const SUBSTAT_COUNT: Record<Tier, number> = { D: 0, C: 1, B: 1, A: 2, S: 3 };

const CRAFTABLE: readonly Tier[] = ["B", "A", "S"];
const TIER_MARK: Record<Tier, string> = { D: "I", C: "II", B: "III", A: "IV", S: "V" };

const NAMES: Record<Manufacturer, Record<ModuleSlot, [string, string]>> = {
  HEX: { drive: ["Kettenantrieb", "Tracked Drive"], weapon: ["Kinetikgeschütz", "Kinetic Cannon"], armor: ["Wolframplatte", "Tungsten Plate"], core: ["Reaktorkern", "Reactor Core"], sensor: ["Zielradar", "Targeting Radar"] },
  KRG: { drive: ["Sprungservo", "Jump Servo"], weapon: ["Plasmaklinge", "Plasma Blade"], armor: ["Faserpanzer", "Fiber Armor"], core: ["Servokern", "Servo Core"], sensor: ["Klingenoptik", "Blade Optics"] },
  VLK: { drive: ["Vektorantrieb", "Vector Drive"], weapon: ["Präzisionsstrahler", "Precision Emitter"], armor: ["Silberhülle", "Silver Shell"], core: ["Fokuskern", "Focus Core"], sensor: ["Gyrosensor", "Gyro Sensor"] },
  OMC: { drive: ["Schwarmantrieb", "Swarm Drive"], weapon: ["Nanowerfer", "Nano Launcher"], armor: ["Kollektivgehäuse", "Collective Casing"], core: ["Verbundkern", "Link Core"], sensor: ["Schwarmauge", "Swarm Eye"] },
  RDL: { drive: ["Turboschub", "Turbo Boost"], weapon: ["Schrottkanone", "Scrap Cannon"], armor: ["Rostpanzer", "Rust Plating"], core: ["Sprengkern", "Blast Core"], sensor: ["Wackelvisier", "Rattle Sight"] },
};

const SLOT_TEXT: Record<ModuleSlot, [string, string]> = {
  drive: ["Antriebsmodul: erhöht das Ausweichen.", "Drive module: raises evasion."],
  weapon: ["Waffenmodul: erhöht den Angriff.", "Weapon module: raises attack."],
  armor: ["Panzerungsmodul: erhöht das Leben.", "Armor module: raises HP."],
  core: ["Kernmodul: erhöht die kritische Trefferchance.", "Core module: raises critical hit chance."],
  sensor: ["Sensormodul: erhöht die Genauigkeit.", "Sensor module: raises accuracy."],
};

/** 125 module definitions: 5 manufacturers × 5 slots × 5 tiers; 75 of them (B/A/S) are craftable blueprints (D21). */
export const MODULES: readonly ModuleDef[] = MANUFACTURER_IDS.flatMap((mfr) =>
  MODULE_SLOTS.flatMap((slot) =>
    TIERS.map((tier): ModuleDef => {
      const main = SLOT_MAIN[slot];
      const n = NAMES[mfr][slot];
      return {
        id: `mod.${mfr.toLowerCase()}_${slot}_${tier.toLowerCase()}`,
        slot,
        tier,
        mfr,
        name: { de: `${n[0]} Mk ${TIER_MARK[tier]}`, en: `${n[1]} Mk ${TIER_MARK[tier]}` },
        main: { stat: main.stat, base: Math.round(main.base * MODULE_TIER_MULT[tier] * 10) / 10 },
        substatPool: SLOT_SUBSTATS[slot],
        blueprint: CRAFTABLE.includes(tier),
        description: { de: SLOT_TEXT[slot][0], en: SLOT_TEXT[slot][1] },
      };
    }),
  ),
);

const moduleMap = new Map(MODULES.map((m) => [m.id, m]));

export function moduleById(id: string): ModuleDef {
  const m = moduleMap.get(id);
  if (!m) throw new Error(`unknown module ${id}`);
  return m;
}

export function hasModule(id: string): boolean {
  return moduleMap.has(id);
}

export const BLUEPRINT_MODULE_IDS: readonly string[] = MODULES.filter((m) => m.blueprint).map((m) => m.id);

/** Set bonuses: two pieces of one manufacturer +4 % ATK, four pieces the manufacturer effect (R26). */
export const SET_TWO_PIECE_ATK_PCT = 4;
export type SetEffectKey = "hpPct" | "speedPct" | "critPct" | "auraPct" | "dmgVsShieldedPct";
export const SET_EFFECTS: Record<Manufacturer, { key: SetEffectKey; pct: number; name: { de: string; en: string } }> = {
  HEX: { key: "hpPct", pct: 10, name: { de: "Hexa-Panzerverbund", en: "Hexa Armor Link" } },
  KRG: { key: "speedPct", pct: 8, name: { de: "Kurogane-Takt", en: "Kurogane Tempo" } },
  VLK: { key: "critPct", pct: 8, name: { de: "Valkyr-Fokus", en: "Valkyr Focus" } },
  OMC: { key: "auraPct", pct: 15, name: { de: "Kollektiv-Resonanz", en: "Collective Resonance" } },
  RDL: { key: "dmgVsShieldedPct", pct: 12, name: { de: "Redline-Sprengkraft", en: "Redline Breach" } },
};

/** Main stat value after enhancement: base × (1 + 5 % × step). */
export function moduleMainValue(def: ModuleDef, enhance: number): number {
  return Math.round(def.main.base * (1 + (ENHANCE_STEP_PCT / 100) * enhance) * 100) / 100;
}

/** Substat value for a rolled stat on a module of `tier`. */
export function substatValue(stat: ModuleStat, tier: Tier): number {
  return Math.round(SUBSTAT_BASE[stat] * MODULE_TIER_MULT[tier] * 10) / 10;
}

/**
 * Highest enhancement step of a tier (D136): only S goes past +15, by the steps the Overforge research grants
 * (`module.overforge`, OVERFORGE_STEPS_PER_LEVEL per research level), up to +40.
 */
export function enhanceCap(tier: Tier, overforgeSteps: number): number {
  if (tier !== "S") return ENHANCE_MAX;
  return Math.min(ENHANCE_MAX_S, ENHANCE_MAX + Math.max(0, Math.floor(overforgeSteps)));
}

/** Enhancement cores for one step past +15: ceil((step − 15)^1.5) — 1 at +16, 32 at +25, 125 at +40 (D136). */
export function enhanceCores(step: number): number {
  return step <= ENHANCE_MAX ? 0 : Math.ceil(Math.pow(step - ENHANCE_MAX, 1.5));
}

/**
 * Enhancement cost from `step − 1` to `step`: Scrip and Nano-Kits rising with tier and step (1..15); S continues to
 * +40, where the Scrip keeps climbing 25 % a step and every step also takes enhancement cores.
 */
export function enhanceCost(tier: Tier, step: number): { SCR: number; nano: number; cores: number } {
  if (step < 1 || step > (tier === "S" ? ENHANCE_MAX_S : ENHANCE_MAX)) throw new RangeError(`no enhancement step ${step}`);
  const mult = TIER_STAT_MULT[tier];
  return { SCR: Math.round((200 * mult * Math.pow(1.25, step - 1)) / 10) * 10, nano: Math.max(1, Math.ceil((step * mult) / 3)), cores: enhanceCores(step) };
}

/** Craft cost per craftable tier: one blueprint, Alloy, Nano-Kits and a timer. */
export function craftCost(tier: Tier): { ALY: number; nano: number; blueprints: number; timeSec: number } {
  switch (tier) {
    case "B":
      return { ALY: 2_000, nano: 5, blueprints: 1, timeSec: 1_800 };
    case "A":
      return { ALY: 8_000, nano: 15, blueprints: 1, timeSec: 7_200 };
    case "S":
      return { ALY: 30_000, nano: 40, blueprints: 1, timeSec: 28_800 };
    default:
      throw new RangeError(`tier ${tier} modules are not craftable`);
  }
}

/** Nano-Kits returned when dismantling, plus half of the Nano-Kits spent on enhancement. */
export const DISMANTLE_NANO: Record<Tier, number> = { D: 1, C: 2, B: 3, A: 8, S: 20 };

/** Armory `maxModuleTier` attribute (1..5) → highest craftable tier. */
export function tierForArmoryIndex(index: number): Tier {
  const asc: Tier[] = ["D", "C", "B", "A", "S"];
  return asc[Math.max(0, Math.min(asc.length - 1, index - 1))]!;
}
